import omit from 'lodash/omit';
import path from 'path-browserify';

import {
  PREFERENCES_NOTE,
  RELEASE_NOTES_NOTE,
  ReservedNoteTab,
} from 'src/common/constants/notebook/noteConstants';
import {
  NoteStateMap,
  NotebookState,
} from 'src/common/store/notebook/NotebookState';
import {
  NoteState,
  NoteStorageType,
} from 'src/common/store/notebook/NoteState';
import { getTabId } from 'src/common/utils/notes/tabId';
import { safelyGet } from 'src/common/utils/safelyGet';
import { InitialData } from 'src/types';

const RELEASE_NOTES_NOTE_ID = "__RELEASE_NOTES__/What's-New";
const USER_PREFERENCES_NOTE_ID = '__USER_PREFERENCES__/User-Settings.json';

// eslint-disable-next-line import/no-default-export
export default (data: InitialData) => {
  const oldNotebook: Partial<NotebookState> = safelyGet(data, ['notebook'], {});
  const oldNotes: NoteStateMap = safelyGet(oldNotebook, ['notes'], {});
  const newNoteIdsByOldNoteId: Record<string, string> = {};

  const newNotes: NoteStateMap = Object.entries(oldNotes).reduce(
    (acc, [noteId, note]) => {
      const isUntitled = safelyGet(note.editorSettings, ['isUntitled']) || true;
      const oldStorageType =
        safelyGet(note.editorSettings, ['storageType']) || NoteStorageType.NONE;
      const name = isUntitled ? path.basename(note.id) : note.id;
      let updatedStorageType: NoteStorageType = oldStorageType;
      if (
        noteId === RELEASE_NOTES_NOTE_ID ||
        noteId === USER_PREFERENCES_NOTE_ID
      ) {
        updatedStorageType = NoteStorageType.RESERVED;
      } else if (
        oldStorageType !== NoteStorageType.FILESYSTEM &&
        oldStorageType !== NoteStorageType.STARDOG
      ) {
        updatedStorageType = NoteStorageType.NONE;
      }

      const newNote = new NoteState(
        updatedStorageType,
        name,
        getTabId(updatedStorageType, name)
      );
      newNote.isPreview = note.isPreview;
      newNote.editorSettings = omit(
        note.editorSettings,
        'isUntitled',
        'storageType'
      );
      newNote.resultSettings = note.resultSettings;
      newNote.totalQueries = note.totalQueries;
      newNote.pane = note.pane;
      newNote.isUntitled =
        safelyGet(note.editorSettings, ['isUntitled']) || true;
      newNote.target =
        oldStorageType === NoteStorageType.FILESYSTEM ||
        oldStorageType === NoteStorageType.STARDOG
          ? note.id
          : null;
      newNote.voiceboxSettings = note.voiceboxSettings;
      newNote.storageType = updatedStorageType;

      if (updatedStorageType === NoteStorageType.RESERVED) {
        if (note.id === RELEASE_NOTES_NOTE_ID) {
          newNote.id = RELEASE_NOTES_NOTE.id;
          newNote.name = ReservedNoteTab.RELEASE_NOTES;
        } else if (note.id === USER_PREFERENCES_NOTE_ID) {
          newNote.id = PREFERENCES_NOTE.id;
          newNote.name = ReservedNoteTab.PREFERENCES;
        }
      } else if (newNote.isUntitled) {
        newNote.id = getTabId(updatedStorageType, name);
        newNote.name = name;
      } else {
        newNote.id = getTabId(updatedStorageType, name);
        newNote.name = name;
      }

      newNoteIdsByOldNoteId[noteId] = newNote.id;
      acc[newNote.id] = newNote;
      return acc;
    },
    {}
  );

  const activeNoteId = oldNotebook.activeNoteId
    ? newNoteIdsByOldNoteId[oldNotebook.activeNoteId]
    : null;
  const noteIds = oldNotebook?.noteIds.map(
    (noteId) => newNoteIdsByOldNoteId[noteId] || noteId
  );

  return {
    ...data,
    version: '5.1.0',
    notebook: {
      ...oldNotebook,
      notes: newNotes,
      activeNoteId,
      noteIds,
    },
  };
};
